/**
 * VehiclesTable - Vehicle listing with inline editing
 * Modern CRM style - compact layout
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, AlertCircle, User } from 'lucide-react';
import { EditableTable, type Column } from '../inline-edit/EditableTable';
import { Input } from '../ui';

/**
 * Vehicle data with customer info and version
 */
export interface VehicleTableItem {
  id: string;
  registration: string;
  make: string;
  model: string;
  year?: number | null;
  color?: string | null;
  mileage?: number | null;
  customerId: string;
  createdAt?: Date | number | null;
  updatedAt?: Date | number | null;
  version: number;
  customer?: {
    id: string;
    name: string;
    phone: string;
  };
}

interface VehiclesTableProps {
  vehicles: VehicleTableItem[];
  isLoading: boolean;
  error: Error | null;
  total: number;
  limit: number;
  offset: number;
  search: string;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSearchChange: (search: string) => void;
  onPageChange: (offset: number) => void;
  onSortChange: (sortBy: string, sortDir: 'asc' | 'desc') => void;
  onFieldUpdate: (vehicleId: string, field: string, value: string, version: number) => Promise<VehicleTableItem>;
  onUpdateError?: (vehicleId: string, field: string, error: Error) => void;
}

export function VehiclesTable({
  vehicles,
  isLoading,
  error,
  total,
  limit,
  offset,
  search,
  sortBy,
  sortDir,
  onSearchChange,
  onPageChange,
  onSortChange,
  onFieldUpdate,
  onUpdateError,
}: VehiclesTableProps) {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState(search);

  // Handle search submit
  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSearchChange(searchInput);
  }, [searchInput, onSearchChange]);

  // Handle sort toggle
  const handleSort = useCallback((key: string) => {
    if (sortBy === key) {
      onSortChange(key, sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(key, 'asc');
    }
  }, [sortBy, sortDir, onSortChange]);

  // Handle inline save
  const handleSave = useCallback(async (item: VehicleTableItem, field: string, value: string) => {
    await onFieldUpdate(item.id, field, value, item.version);
  }, [onFieldUpdate]);

  // Handle inline error
  const handleError = useCallback((item: VehicleTableItem, field: string, err: Error) => {
    onUpdateError?.(item.id, field, err);
  }, [onUpdateError]);

  // Handle row click
  const handleRowClick = useCallback((item: VehicleTableItem) => {
    navigate(`/vehicles/${item.id}`);
  }, [navigate]);

  // Column definitions - compact CRM style
  const columns: Column<VehicleTableItem>[] = [
    {
      key: 'registration',
      header: 'Matricula',
      width: '100px',
      sortable: true,
      editable: {
        type: 'text',
        placeholder: 'Matricula',
        getValue: (item) => item.registration,
      },
      cellClassName: 'text-xs font-mono font-bold text-gray-900',
    },
    {
      key: 'make',
      header: 'Marca',
      width: '100px',
      sortable: true,
      editable: {
        type: 'text',
        placeholder: 'Marca',
        getValue: (item) => item.make,
      },
      cellClassName: 'text-xs text-gray-700',
    },
    {
      key: 'model',
      header: 'Modelo',
      width: '100px',
      sortable: true,
      editable: {
        type: 'text',
        placeholder: 'Modelo',
        getValue: (item) => item.model,
      },
      cellClassName: 'text-xs text-gray-700',
    },
    {
      key: 'year',
      header: 'Ano',
      width: '60px',
      sortable: true,
      editable: {
        type: 'number',
        placeholder: 'Ano',
        getValue: (item) => item.year,
      },
      cellClassName: 'text-xs text-gray-600',
    },
    {
      key: 'color',
      header: 'Color',
      width: '80px',
      editable: {
        type: 'text',
        placeholder: 'Color',
        getValue: (item) => item.color,
      },
      cellClassName: 'text-xs text-gray-600',
    },
    {
      key: 'mileage',
      header: 'Km',
      width: '90px',
      editable: {
        type: 'number',
        placeholder: 'Kilometraje',
        getValue: (item) => item.mileage,
        formatter: (value) => value ? `${Number(value).toLocaleString('es-ES')}` : '-',
      },
      cellClassName: 'text-xs text-gray-600',
    },
    {
      key: 'customer',
      header: 'Propietario',
      width: '150px',
      render: (item) => (
        item.customer ? (
          <div className="flex items-center gap-1.5">
            <User className="w-3 h-3 text-gray-400" />
            <div className="min-w-0">
              <div className="text-xs font-medium text-gray-900 truncate max-w-[120px]">
                {item.customer.name}
              </div>
              <div className="text-[10px] text-gray-500">{item.customer.phone}</div>
            </div>
          </div>
        ) : (
          <span className="text-xs text-gray-400">-</span>
        )
      ),
    },
    {
      key: 'updatedAt',
      header: 'Actualizado',
      width: '90px',
      sortable: true,
      render: (item) => (
        item.updatedAt ? (
          <span className="text-xs text-gray-500">
            {new Date(item.updatedAt).toLocaleDateString('es-ES', {
              day: '2-digit',
              month: 'short',
            })}
          </span>
        ) : '-'
      ),
    },
  ];

  // Pagination
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-3">
      {/* Search and filters */}
      <div className="flex items-center gap-3">
        <form onSubmit={handleSearchSubmit} className="flex-1 max-w-sm">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Buscar vehiculo..."
              className="pl-8 h-8 text-sm"
            />
          </div>
        </form>

        <div className="text-xs text-gray-500">
          {total > 0 ? (
            <>
              {offset + 1}-{Math.min(offset + limit, total)} de {total}
            </>
          ) : (
            'Sin resultados'
          )}
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
          <AlertCircle className="w-4 h-4" />
          {error.message}
        </div>
      )}

      {/* Table */}
      <EditableTable
        columns={columns}
        data={vehicles}
        getRowKey={(item) => item.id}
        onSave={handleSave}
        onError={handleError}
        loading={isLoading}
        emptyMessage="No hay vehículos registrados"
        onRowClick={handleRowClick}
        sortBy={sortBy}
        sortDirection={sortDir}
        onSort={handleSort}
        className="shadow-sm"
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <button
            disabled={offset === 0}
            onClick={() => onPageChange(Math.max(0, offset - limit))}
            className="text-xs text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed"
          >
            Anterior
          </button>

          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = i + Math.max(1, Math.min(currentPage - 2, totalPages - 4));
              if (pageNum > totalPages) return null;
              return (
                <button
                  key={pageNum}
                  onClick={() => onPageChange((pageNum - 1) * limit)}
                  className={`w-6 h-6 text-xs rounded ${
                    currentPage === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            disabled={offset + limit >= total}
            onClick={() => onPageChange(offset + limit)}
            className="text-xs text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed"
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
}
